import { TeamWith } from "@redotech/account-common/base-team";
import { getCurrencySymbol } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import {
  CollectionSettings,
  DEFAULT_PRODUCT_TITLE,
  ExtendedWarrantyOffering,
  type ExtendedWarrantySettings,
} from "@redotech/redo-return-warranties-common/extended-warranty";
import {
  InsertionPosition,
  insertionPositionToDisplayText,
} from "@redotech/redo-storefront-common/insertion-position";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import { Card, CollapseSubsection } from "@redotech/redo-web/card";
import { CodeEditorInputField } from "@redotech/redo-web/code-editor";
import { Divider } from "@redotech/redo-web/divider";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { LabeledInput, LabelTheme } from "@redotech/redo-web/labeled-input";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import {
  minMaxValidator,
  Pricing,
  pricingDefault,
  pricingFormValues,
  pricingRulesConvertTeamToValue,
  pricingRulesConvertValueToTeam,
} from "@redotech/redo-web/settings-elements/dynamic-pricing";
import { Text } from "@redotech/redo-web/text";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import { Fragment, memo, useContext } from "react";
import { TeamContext } from "../../team";
import { CollectionAutocomplete } from "../returns-package-protection/collections-autocomplete";
import {
  experienceDesignDefault,
  experienceDesignForm,
  experienceDesignTeamToFormValue,
} from "./experience-design";

export const extendedWarrantyOfferingForm = groupInput(
  {
    timeId: input<string>(),
    timeTitle: input<string>(),
    daysExtended: input<string>(),
    ...pricingFormValues,
  },
  { validator: minMaxValidator },
);

export const extendedWarrantyCollectionForm = groupInput({
  collectionHandle: input<string>(),
  collectionName: input<string>(),
  validOfferings: input<string[]>({ equal: arrayEqual(stringEqual) }),
  offerings: listInput(
    () => extendedWarrantyOfferingForm,
    () => ({
      timeId: "",
      timeTitle: "",
      daysExtended: "0",
      collectionHandle: "",
      collectionName: "",
      ...pricingDefault,
    }),
    (_, index) => index,
  ),
});

export const widgetConfigForm = groupInput({
  variantSelector: input<string>(),
  variantSelectorInsertPosition: input<InsertionPosition>(),
  clickListenerSelector: input<string>(),
  customPdpCss: input<string>(),
  customToggleCss: input<string>(),
  customModalCss: input<string>(),
  customPopupCss: input<string>(),
});

export const widgetConfigFormDefault = {
  variantSelector: "",
  variantSelectorInsertPosition: InsertionPosition.AFTER,
  clickListenerSelector: "",
  customPdpCss: "",
  customToggleCss: "",
  customModalCss: "",
  customPopupCss: "",
};

export const extendedWarrantySettingsForm = groupInput(
  {
    enabled: input<boolean>(),
    registrationUpsellEnabled: input<boolean>(),
    validCollections: input<string[]>({ equal: arrayEqual(stringEqual) }),
    productTitle: input<string>(),
    extendedWarrantyRevSharePercentage: input<string>(),
    warrantyActivationDelay: input<string>(),
    experienceDesign: experienceDesignForm,
    widgetConfig: widgetConfigForm,
    disableRevshareOnDiscountedProducts: input<boolean>(),
    collectionSettings: listInput(
      () => extendedWarrantyCollectionForm,
      () => ({
        collectionHandle: "",
        collectionName: "",
        validOfferings: [],
        offerings: [],
      }),
      (_, index) => index,
    ),
  },
  {
    validator: (value) => {
      const errors = [];
      if (value.enabled && value.collectionSettings.length === 0) {
        errors.push("At least one collection must be added");
      }
      if (
        value.enabled &&
        value.collectionSettings.some(
          (collection) => collection.offerings.length === 0,
        )
      ) {
        errors.push("At least one offering must be added");
      }
      return errors;
    },
  },
);

export type ExtendedWarrantyCollectionValue = InputSchema.Value<
  typeof extendedWarrantyCollectionForm
>;
export type ExtendedWarrantyCollectionForm = InputSchema.Input<
  typeof extendedWarrantyCollectionForm
>;

export type ExtendedWarrantySettingsForm = InputSchema.Input<
  typeof extendedWarrantySettingsForm
>;

export type ExtendedWarrantySettingsValue = InputSchema.Value<
  typeof extendedWarrantySettingsForm
>;

export const extendedWarrantySettingsDefault = {
  enabled: false,
  registrationUpsellEnabled: false,
  extendedWarrantyRevSharePercentage: "1",
  disableRevshareOnDiscountedProducts: false,
  validCollections: [],
  productTitle: "",
  collectionSettings: [],
  warrantyActivationDelay: "0",
  experienceDesign: experienceDesignDefault,
  widgetConfig: widgetConfigFormDefault,
};

export function extendedWarrantyTeamToFormValue(
  team?: TeamWith<{ extendedWarranties?: ExtendedWarrantySettings }>,
): ExtendedWarrantySettingsValue {
  return {
    enabled: team?.settings.extendedWarranties?.enabled || false,
    registrationUpsellEnabled:
      team?.settings.extendedWarranties?.upsellEnabled || false,
    widgetConfig: {
      variantSelector: team?.settings.extendedWarranties?.variantSelector || "",
      variantSelectorInsertPosition:
        team?.settings.extendedWarranties?.variantSelectorInsertPosition ||
        InsertionPosition.AFTER,
      clickListenerSelector:
        team?.settings.extendedWarranties?.clickListenerSelector || "",
      customPdpCss: team?.settings.extendedWarranties?.customPdpCss || "",
      customToggleCss: team?.settings.extendedWarranties?.customToggleCss || "",
      customModalCss: team?.settings.extendedWarranties?.customModalCss || "",
      customPopupCss: team?.settings.extendedWarranties?.customPopupCss || "",
    },
    validCollections: team?.settings.extendedWarranties?.validCollections || [],
    productTitle: team?.settings.extendedWarranties?.productTitle || "",
    disableRevshareOnDiscountedProducts:
      team?.settings.extendedWarranties?.disableRevshareOnDiscountedProducts ||
      false,
    extendedWarrantyRevSharePercentage: team?.settings.extendedWarranties
      ?.revSharePercentage
      ? String(team?.settings.extendedWarranties?.revSharePercentage * 100)
      : "0",
    warrantyActivationDelay: String(
      team?.settings.extendedWarranties?.warrantyActivationDelay || 0,
    ),
    experienceDesign: experienceDesignTeamToFormValue(team),
    collectionSettings:
      team?.settings.extendedWarranties?.collectionSettings?.map(
        (collectionSettings) => ({
          collectionHandle: collectionSettings.collectionHandle,
          collectionName: collectionSettings.collectionName || "",
          validOfferings: collectionSettings.validOfferings || [],
          offerings: collectionSettings.offerings.map((offering) => ({
            timeId: offering.timeId,
            daysExtended: offering.daysExtended
              ? String(offering.daysExtended)
              : "0",
            timeTitle: offering.timeTitle,
            minPrice: offering.minPrice,
            maxPrice: offering.maxPrice,
            percentage: offering.percentage,
            internationalPricingEnabled: offering.internationalPricingEnabled,
            pricingRuleSet: pricingRulesConvertTeamToValue([
              offering.pricingRuleSet,
            ]),
          })),
        }),
      ) || [],
  };
}

const LIFETIME_DAYS = 36_500;

export function extendedWarrantyFormValueToTeam(
  extendedWarranties: ExtendedWarrantySettingsValue,
) {
  return {
    enabled: extendedWarranties.enabled,
    upsellEnabled: extendedWarranties.registrationUpsellEnabled,
    variantSelector:
      extendedWarranties.widgetConfig.variantSelector || undefined,
    variantSelectorInsertPosition:
      extendedWarranties.widgetConfig.variantSelectorInsertPosition,
    clickListenerSelector:
      extendedWarranties.widgetConfig.clickListenerSelector || undefined,
    validCollections: extendedWarranties.validCollections,
    productTitle: extendedWarranties.productTitle || undefined,
    warrantyActivationDelay: Number(extendedWarranties.warrantyActivationDelay),
    revSharePercentage:
      Number(extendedWarranties.extendedWarrantyRevSharePercentage) / 100,
    collectionSettings: extendedWarranties.collectionSettings.map(
      (collectionSettings) => ({
        ...collectionSettings,
        offerings: collectionSettings.offerings.map((offering) => ({
          ...offering,
          daysExtended:
            offering.timeId === "lifetime"
              ? LIFETIME_DAYS
              : parseInt(offering.daysExtended),
          pricingRuleSet: pricingRulesConvertValueToTeam(
            offering.pricingRuleSet,
          )?.[0],
        })) as ExtendedWarrantyOffering[],
      }),
    ) as CollectionSettings[],
    disableRevshareOnDiscountedProducts:
      extendedWarranties.disableRevshareOnDiscountedProducts,
    customPdpCss: extendedWarranties.widgetConfig.customPdpCss || undefined,
    customToggleCss:
      extendedWarranties.widgetConfig.customToggleCss || undefined,
    customModalCss: extendedWarranties.widgetConfig.customModalCss || undefined,
    customPopupCss: extendedWarranties.widgetConfig.customPopupCss || undefined,
    logo: extendedWarranties.experienceDesign.logo
      ? extendedWarranties.experienceDesign.logo.toString()
      : "",
    sideImage: extendedWarranties.experienceDesign.sideImage
      ? extendedWarranties.experienceDesign.sideImage.toString()
      : "",
    modalTitle: extendedWarranties.experienceDesign.modalTitle || undefined,
    modalCoverageHeader:
      extendedWarranties.experienceDesign.modalCoverageHeader || undefined,
    modalDescription:
      extendedWarranties.experienceDesign.modalDescription || undefined,
    modalBulletPoints: extendedWarranties.experienceDesign.modalBulletPoints
      ? extendedWarranties.experienceDesign.modalBulletPoints.split("\n")
      : undefined,
    modalPlanHeader:
      extendedWarranties.experienceDesign.modalPlanHeader || undefined,
    modalSeeDetailsUrl:
      extendedWarranties.experienceDesign.modalSeeDetailsUrl || undefined,
    pdpExperience: extendedWarranties.experienceDesign.pdpExperience,
    defaultSelection: extendedWarranties.experienceDesign.pdpDefaultSelection,
    pdpRenderMethod: extendedWarranties.experienceDesign.pdpRenderMethod,
    pdpInsertPosition: extendedWarranties.experienceDesign.pdpInsertPosition,
    pdpNoCoverageType: extendedWarranties.experienceDesign.pdpNoCoverageType,
    pdpSelector: extendedWarranties.experienceDesign.pdpSelector || undefined,
    pdpTitle: extendedWarranties.experienceDesign.pdpTitle || undefined,
    pdpLabel: extendedWarranties.experienceDesign.pdpLabel || undefined,
    pdpCheckboxTitle:
      extendedWarranties.experienceDesign.pdpCheckboxTitle || undefined,
    pdpCoverageDisabledLabel:
      extendedWarranties.experienceDesign.pdpCoverageDisabledLabel || undefined,
    pdpDescription:
      extendedWarranties.experienceDesign.pdpDescription || undefined,
    pdpNoCoveragePrice:
      extendedWarranties.experienceDesign.pdpNoCoveragePrice || undefined,
    pdpAddToCartDescription:
      extendedWarranties.experienceDesign.pdpAddToCartDescription || undefined,
    pdpAddedToCartTitle:
      extendedWarranties.experienceDesign.pdpAddedToCartTitle || undefined,
    pdpAddedToCartDescription:
      extendedWarranties.experienceDesign.pdpAddedToCartDescription ||
      undefined,
    pdpCompactBlurbTitle:
      extendedWarranties.experienceDesign.pdpCompactBlurbTitle || undefined,
    pdpBlurbType: extendedWarranties.experienceDesign.pdpBlurbType,
    pdpTilesSize: extendedWarranties.experienceDesign.pdpTilesSize,
    pdpRadioOptionTitle:
      extendedWarranties.experienceDesign.pdpRadioOptionTitle || undefined,
    pdpRadioOptionType: extendedWarranties.experienceDesign.pdpRadioOptionType,
    cartToggleExperience:
      extendedWarranties.experienceDesign.cartToggleExperience,
    cartToggleModalNotAddedDescription:
      extendedWarranties.experienceDesign.cartToggleModalNotAddedDescription ||
      undefined,
    cartToggleModalAddedDescription:
      extendedWarranties.experienceDesign.cartToggleModalAddedDescription ||
      undefined,
    cartToggleAddedDescription:
      extendedWarranties.experienceDesign.cartToggleAddedDescription ||
      undefined,
    cartToggleNotAddedDescription:
      extendedWarranties.experienceDesign.cartToggleNotAddedDescription ||
      undefined,
    popupExperience: extendedWarranties.experienceDesign.popupExperience,
    popupClickListenerSelector:
      extendedWarranties.experienceDesign.popupClickListenerSelector ||
      undefined,
    popupFoundSelector:
      extendedWarranties.experienceDesign.popupFoundSelector || undefined,
    popupTitle: extendedWarranties.experienceDesign.popupTitle || undefined,
    popupTitleSubtext:
      extendedWarranties.experienceDesign.popupTitleSubtext || undefined,
    popupDescription:
      extendedWarranties.experienceDesign.popupDescription || undefined,
    popupButtonAccept:
      extendedWarranties.experienceDesign.popupButtonAccept || undefined,
    popupButtonDecline:
      extendedWarranties.experienceDesign.popupButtonDecline || undefined,
  };
}

export const ExtendedWarranties = memo(function ExtendedWarranties({
  input,
}: {
  input: ExtendedWarrantySettingsForm;
}) {
  const team = useContext(TeamContext);
  const hasSufficientAccess = team?.accessScope.includes(
    "write_cart_transforms",
  );

  const {
    enabled,
    registrationUpsellEnabled,
    extendedWarrantyRevSharePercentage,
    validCollections,
    collectionSettings,
    warrantyActivationDelay,
    widgetConfig,
    productTitle,
    disableRevshareOnDiscountedProducts,
  } = input.inputs;

  const {
    variantSelector,
    variantSelectorInsertPosition,
    clickListenerSelector,
    customPdpCss,
    customToggleCss,
    customModalCss,
    customPopupCss,
  } = widgetConfig.inputs;

  const setValue = useHandler(
    (collections: { id: string; title: string; handle: string }[]) => {
      input.inputs.validCollections.setValue(
        collections.map((collection) => collection.handle),
      );
      input.inputs.collectionSettings.setValue(
        collections.map((collection) => ({
          collectionHandle: collection.handle,
          collectionName: collection.title,
          validOfferings:
            input.value.collectionSettings.find(
              (collectionSettings) =>
                collectionSettings.collectionHandle === collection.handle,
            )?.validOfferings || [],
          offerings:
            input.value.collectionSettings.find(
              (collectionSettings) =>
                collectionSettings.collectionHandle === collection.handle,
            )?.offerings || [],
        })),
      );
    },
  );

  return (
    <Card title="Extended Warranties">
      <section className={gridCss.grid}>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <RedoToggle
            disabled={!hasSufficientAccess}
            label="Extended warranties enabled"
            setValue={enabled.setValue}
            size="sm"
            value={enabled.value}
          />
          {!hasSufficientAccess && (
            <Text fontSize="sm" pt="sm">
              The team does not have sufficient access to enable extended
              warranties. Please ask an admin to update the team's permissions
              through the merchant portal.
            </Text>
          )}
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <RedoToggle
            disabled={!hasSufficientAccess}
            label="Registration upsell enabled"
            setValue={registrationUpsellEnabled.setValue}
            size="sm"
            value={registrationUpsellEnabled.value}
          />
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormTextInput
            description="Percentage of total extended warranty purchase price that Redo keeps. Don't touch this unless you have talked to Dan."
            input={extendedWarrantyRevSharePercentage}
            label="Extended warranty rev share percentage"
            max={100}
            min={0}
            step={1}
            suffix="%"
            type="number"
          />
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <RedoToggle
            disabled={!hasSufficientAccess}
            label="Disable rev share on discounted products"
            setValue={disableRevshareOnDiscountedProducts.setValue}
            size="sm"
            value={disableRevshareOnDiscountedProducts.value}
          />
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6M)}>
          <FormTextInput
            description="This is the title of the extended warranty product that will be created on the store."
            input={productTitle}
            label="Product title"
            placeholder={DEFAULT_PRODUCT_TITLE}
          />
        </div>
        <div className={gridCss.span12}>
          <LabeledInput
            description="Coverage will be offered for these Shopify collections. The first 100 collections are shown by default. To access more, search by title."
            label="Included collections"
          >
            <CollectionAutocomplete
              keyFn={(collection) => collection.handle}
              setValueCollections={setValue}
              value={validCollections.value}
            />
          </LabeledInput>
        </div>
        <div className={gridCss.span12}>
          {input.errors.map((error, index) => (
            <Text fontSize="sm" key={index} pt="sm" textColor="warning">
              {error}
            </Text>
          ))}
        </div>
        <div className={gridCss.span12}>
          <CollapseSubsection title="Collection settings">
            <FormTextInput
              description="The number of days that the manufacturer's warranty lasts."
              input={warrantyActivationDelay}
              label="Manufacturer's warranty length"
              min={0}
              step={1}
              suffix="days"
              type="number"
            />
            {collectionSettings.value.map((collectionSettings, index) => (
              <ExtendedWarrantiesCollection
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                input={input.inputs.collectionSettings.inputs[index]}
                key={collectionSettings.collectionHandle}
              />
            ))}
          </CollapseSubsection>
        </div>
        <div className={gridCss.span12}>
          <CollapseSubsection title="Advanced options">
            <FormTextInput
              description="You can use %variant_id% as placeholder."
              input={variantSelector}
              label="Variant selector for the extended warranty toggle."
            />
            <FormSelectDropdown
              input={variantSelectorInsertPosition}
              label="Variant selector insert position"
              options={Object.values(InsertionPosition)}
            >
              {(option) => insertionPositionToDisplayText[option]}
            </FormSelectDropdown>
            <FormTextInput
              input={clickListenerSelector}
              label="Click listener selector to trigger rerender."
            />
            <CodeEditorInputField
              label="Custom PDP CSS"
              language="css"
              setValue={customPdpCss.setValue}
              value={customPdpCss.value ?? ""}
            />
            <CodeEditorInputField
              label="Custom Toggle CSS"
              language="css"
              setValue={customToggleCss.setValue}
              value={customToggleCss.value ?? ""}
            />
            <CodeEditorInputField
              label="Custom Modal CSS"
              language="css"
              setValue={customModalCss.setValue}
              value={customModalCss.value ?? ""}
            />
            <CodeEditorInputField
              label="Custom Popup CSS"
              language="css"
              setValue={customPopupCss.setValue}
              value={customPopupCss.value ?? ""}
            />
          </CollapseSubsection>
        </div>
      </section>
    </Card>
  );
});

export const ExtendedWarrantiesCollection = memo(
  function ExtendedWarrantiesCollection({
    input,
  }: {
    input: ExtendedWarrantyCollectionForm;
  }) {
    const tileOptions: { id: string; title: string; handle: string }[] = [];
    for (let i = 1; i <= 10; i++) {
      tileOptions.push({
        id: i.toString(),
        title: `${i} Year`,
        handle: i.toString(),
      });
    }
    tileOptions.push({ id: "lifetime", title: "Lifetime", handle: "lifetime" });
    const team = useContext(TeamContext);
    const selectedTileOptions = tileOptions.filter((option) =>
      input.value.offerings.some((offering) => offering.timeId === option.id),
    );

    const handleValueChange = useHandler(
      (tileOptions: { id: string; title: string; handle: string }[]) => {
        input.inputs.validOfferings.setValue(
          tileOptions.map((option) => option.id),
        );
        input.inputs.offerings.setValue(
          tileOptions.map((option, index) => ({
            timeId: option.id,
            daysExtended:
              option.id === "lifetime"
                ? String(LIFETIME_DAYS)
                : String(parseInt(option.id) * 365),
            timeTitle: option.title,
            collectionHandle: input.value.collectionHandle,
            collectionName: input.value.collectionName,
            ...(input.inputs.offerings.value.some(
              (offering) => offering.timeId === option.id,
            )
              ? // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                input.inputs.offerings.inputs[index].value
              : pricingDefault),
          })),
        );
      },
    );

    return (
      <>
        <div className={classNames(gridCss.span12)}>
          <Divider />
        </div>
        <div className={gridCss.span12}>
          <strong>{input.value.collectionName}</strong>
        </div>

        <div className={gridCss.span12}>
          <LabeledInput
            description="These are the tiles offered to customers."
            label={`Warranty offerings for ${input.value.collectionName}`}
          >
            <Autocomplete
              getLabel={(option) => option.title}
              keyFn={(option) => option.handle}
              multiple
              options={tileOptions}
              value={selectedTileOptions}
              valueChange={handleValueChange}
            >
              {(option) => option.title}
            </Autocomplete>
          </LabeledInput>
        </div>
        {input.inputs.offerings.inputs.map((offeringInput, index) => (
          <Fragment key={index}>
            <div className={gridCss.span12}>
              <Divider />
            </div>
            <div className={gridCss.span12}>
              <FormTextInput
                input={offeringInput.inputs.timeTitle}
                label="Plan title"
                labelTheme={LabelTheme.THIN}
              />
            </div>
            <div className={gridCss.span12}>
              <FormTextInput
                disabled={offeringInput.value.timeId === "lifetime"}
                input={offeringInput.inputs.daysExtended}
                label="Days extended"
                labelTheme={LabelTheme.THIN}
                min={0}
                step={1}
                type="number"
              />
            </div>
            <div className={gridCss.span12}>
              <Pricing
                adminApp
                canEditSettings
                currencyDisplay={getCurrencySymbol(
                  getShopCurrencyWithDefault({ team }),
                )}
                input={offeringInput}
                maxPriceInput={offeringInput.inputs.maxPrice}
              />
            </div>
          </Fragment>
        ))}
      </>
    );
  },
);
